import React from "react";
import '../styles/MerchantInfo.css'
import {Button, Modal, Spin, Tabs, Tooltip} from "antd";
import {CloudDownloadOutlined, PlusOutlined} from "@ant-design/icons";
import {fetchDocument, getFeConfig} from "../api/api";
import {unstable_batchedUpdates} from "react-dom";
import {resolve} from "@q4us-sw/q4us-ui/lib/Cell";
import {
    AddNewRecordContext,
    AddNewRecordContextProvider,
    CellActionContext,
    EditorContextProvider,
    MerchantInfoContext,
    EditButton, GroupValueContext
} from "./Editor";
import {TableContext} from "@q4us-sw/q4us-ui/lib/TableV2";
import {DocumentItem} from "./ContractDocuments";

export const isSchema = ({type}) => type === 'INLINE_SCHEMA' || type === 'SCHEMA'

export const ItemValue = ({config, value: _value, ...args}) => {
    const [cfg, setCfg] = React.useState()
    const [loading, setLoading] = React.useState(true)
    const [value, setValue] = React.useState([])


    const load = React.useCallback(async () => {
        const {data: {rows} = {}} = await getFeConfig(config.name) || {}
        const v = value == null || Array.isArray(_value) ? _value : [_value]
        unstable_batchedUpdates(() => {
            setCfg(rows)
            setLoading(false)
            setValue(v?.filter(v => v != null))
        })
    }, [config, _value])

    React.useEffect(() => {
        if (isSchema(config)) {
            load().catch(e => {
                console.log(e)
            })
        } else
            setLoading(false)
    }, [config, load])

    return <React.Fragment>
        {(!loading) && cfg && value && value.map((o, i) => <GroupValue key={`composite-${config.name}-${i + 1}`}
                                                                       value={o}
                                                                       config={cfg} schema={config.name}
                                                                       editorGroupName={`${config.title} #${i + 1}`}
                                                                       name={`${config.title} #${i + 1}`} {...args}/>)}
        {(!loading) && cfg && <AddNewRecordContext.Consumer>
            {rec => <div className={`btn-wrap`}><Button
                type="dashed"
                block
                className={`add-new-record-btn`}
                onClick={() => {
                    unstable_batchedUpdates(() => {
                        rec?.setVisible(true)
                        rec?.setConfig(cfg)
                    })
                }}
            >
                <PlusOutlined/>
                {`Add New ${config.title}`}
            </Button></div>}
        </AddNewRecordContext.Consumer>}
        {(!loading) && (!cfg) && (_value != null) && resolve({...config, value: _value})}
    </React.Fragment>

}


export const Item = ({config, data, schema,editable, ...args}) => {


    const edit = editable ? editable(schema, config,data?.[config?.name] ) : (!config?.generated);


    return <tr>
            {(!isSchema(config)) && <td className={`info-label`}>
            <div className={`cell`}>{config?.title}</div>
        </td>}
        <td colSpan={isSchema(config) ? 3 : 1}>
            <div className={`cell ${isSchema(config) ? "schema-cell" : "data-cell"}`}>
                <ItemValue value={data?.[config?.name]} config={config} {...args} editable={editable}/>
            </div>
        </td>
        {(!isSchema(config)) && <td>
            <div className={`cell`}>
                <CellActionContext.Provider value={{config, data: data, schema}}>
                    {(edit) && <EditButton data={data}/>}
                </CellActionContext.Provider>

            </div>
        </td>}
    </tr>
}


export const FileItem = ({config, data: row = {}, schema}) => {
    const {[schema]:obj} = row || {};
    const {[config?.name]:data =  []} = obj || {}
    return <React.Fragment>
        {data.map(({file, name, id}, index)=>{
            return <tr>
                {index === 0 && <td className={`info-label`} rowSpan={data.length}><div className={`cell`}>{config?.title}</div></td>}
                <td><div className={`cell data-cell`}>
                    <Tooltip title={name}>
                        {file}
                    </Tooltip>
                </div></td>
                <td>
                    <div className={`cell`}>
                        <Button icon={<CloudDownloadOutlined/>} onClick={async ()=>{
                            await fetchDocument(row?.identifier, id, file)
                        }}/>

                    </div>
                </td>
            </tr>
        })}
        {data.length === 0 && <tr>
            <td className={`info-label`}><div className={`cell`}>{config?.title}</div></td>
            <td>
                <div className={`cell data-cell`}>N/A</div>
            </td>
            <td/>
        </tr>}
    </React.Fragment>
}


export const GroupValue = ({config, name, value, schema,editorGroupName,component: Component = Item, ...args}) => {
    return <table>
        <thead>
        <tr>
            <td colSpan={3}>
                <div className={`info-title`}>{name}</div>
            </td>
        </tr>
        </thead>
        <GroupValueContext.Provider value={{name: editorGroupName, schema, config,value}}>
            <tbody>
                {value != null && config?.map((o) => {
                    if (schema === 'em_fee_info' && o.name === 'post_sale_fee_class_id' && !value.post_sale_orders_allowed) {
                        return
                    }
                    return (
                        <Component key={o.name} schema={schema} config={o} data={value} {...args} />)
            })}
            </tbody>
        </GroupValueContext.Provider>
    </table>
}


export const Group = ({schema, merchant, name, cfg = [], ...args}) => {
    const [loading, setLoading] = React.useState(schema !== undefined)
    const [config, setConfig] = React.useState(cfg)

    const load = React.useCallback(async () => {
        if (schema) {
            const {data: {rows} = {}} = await getFeConfig(schema) || {}
            unstable_batchedUpdates(() => {
                setConfig(rows)
                setLoading(false)
            })
        }
    }, [schema])

    React.useEffect(() => {
        load().catch(e => {
            console.log(e)
        })
    }, [schema])

    return <Spin spinning={loading}>
        <div className={`info-group`}>
            <GroupValue editorGroupName={`EM #${merchant?.identifier}`} config={config} value={merchant} name={name} schema={schema} {...args}/>
        </div>
    </Spin>
}


const Configs = [
    {
        schema: 'em_basic_info',
        label: 'Basic Information'
    },
    {
        schema: 'em_landed_cost_payment_settings',
        label: 'Landed Cost/Payment Settings'
    },
    {
        schema: 'em_contact_info',
        label: 'Contact Information'
    },
    {
        schema: 'em_fee_info',
        label: 'EAS Fee Information'
    },
    {
        schema: 'em_oss_info',
        label: 'OSS Information'
    },
    {
        schema: 'em_vat_info',
        label: 'VAT Information',
        editable: (schema, field, value)=>{
            if(schema === 'em_vat' && field?.name === 'type' && value === 'fixed_establishment') {
                return false;
            }
            return (!field?.generated);
        }
    },
    {
        schema: 'em_non_eu_vat_registration_info',
        label: 'Non-EU VAT Registrations'
    },
    {
        schema: 'em_store_info',
        label: 'Online Store Information'
    },
    {
        schema: 'em_establishment_info',
        label: 'Establishment Information'
    },
    {
        schema: 'em_additional_info',
        label: 'Additional Info'
    }
]

export const MerchantInfo = ({em}) => {

    return <React.Fragment>
        <AddNewRecordContextProvider>
            <EditorContextProvider>
                <Tabs defaultActiveKey="em_basic_info">
                    {Configs.map(({schema, label, ...args}) => <Tabs.TabPane tab={label} key={schema}>
                        <Group schema={schema} merchant={em} name={label} {...args}/>
                    </Tabs.TabPane>)}
                    <Tabs.TabPane tab={'Attachments'} key={'em_attachments'}>
                        <Group schema={'em_attachments'} merchant={em} name={'Attachments'} component={FileItem} />
                        <Group schema={'em_contract_document'} merchant={em} name={'Contract Documents'} component={DocumentItem} />
                    </Tabs.TabPane>
                </Tabs>
            </EditorContextProvider>
        </AddNewRecordContextProvider>
    </React.Fragment>
}

export const MerchantInfoDialog = (props)=>{
    return <MerchantInfoContext.Consumer>
        {
            info => <TableContext.Consumer>
                {
                    table=><Modal
                        className={`merchant-dialog`}
                        centered
                        closable
                        width={`80vw`}
                        visible={info?.visible}
                        footer={null}
                        style={{height: '80vh'}}
                        title={`Merchant #${info?.data?.identifier}`}
                        onCancel={async () => {
                            const request  = {
                                options: {
                                    filter : table?.customData?.filter,
                                },
                                page: table?.page,
                                schema: table?.props.schema
                            }
                            const {data: {rows = []}} = await table.props.fetchData(request) || {};
                            table?.setData(rows)
                            info?.setVisible(false)
                        }}
                    >
                        <div className={`merchant-info-dialog`}>
                            <MerchantInfo em={info?.data}/>
                        </div>

                    </Modal>
                }
            </TableContext.Consumer>
        }
    </MerchantInfoContext.Consumer>
}



export const MerchantInfoContextProvider = (props) => {
    const [data, setData] = React.useState();
    const [visible, setVisible] = React.useState(false)


    return <MerchantInfoContext.Provider value={{data, setData, setVisible, visible}}>
        {props.children}
    </MerchantInfoContext.Provider>
}
